import { zodResolver } from '@hookform/resolvers/zod';
import {
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { INode } from '../../NewFormBuilder/Context/FormBuilderContext';
import { usePublishedForm } from '../Context/PublishedFormContext';
import { ActionTypes } from '../Reducer';

interface ListProps {
  node: INode;
}

export const ListSchema = z.object({
  inputText: z.string(),
});

export function List({ node }: ListProps) {
  const { dispatch } = usePublishedForm();

  const inputId = node.data.varDataInputId;

  const formsMethods = useForm({
    defaultValues: {
      inputText: '',
    },
    resolver: zodResolver(ListSchema),
  });

  const { control } = formsMethods;

  const watchedValues = useWatch({
    control: formsMethods.control,
  });

  const updateInputText = useCallback(
    (text: string) => {
      if (inputId) {
        dispatch({
          type: ActionTypes.UPDATE_INPUT_TEXT,
          payload: { inputId, inputText: text },
        });
      }
    },
    [inputId, dispatch],
  );

  useEffect(() => {
    if (inputId && watchedValues.inputText !== undefined) {
      updateInputText(watchedValues.inputText);
    }
  }, [watchedValues.inputText, inputId, updateInputText]);

  const handleSingleChange = (value: string) => {
    formsMethods.setValue('inputText', value);
  };

  const handleMultipleChange = (value: string, checked: boolean) => {
    const currentValue = formsMethods.getValues('inputText');
    const currentValues = currentValue
      ? currentValue.split(',').map(item => item.trim())
      : [];

    let newValues: string[];
    if (checked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter(v => v !== value);
    }

    formsMethods.setValue('inputText', newValues.join(', '));
  };

  const handleDropdownChange = (value: string) => {
    formsMethods.setValue('inputText', value);
  };

  const renderDropdown = () => (
    <Controller
      name="inputText"
      control={control}
      render={({ field }) => (
        <FormControl fullWidth>
          <TextField
            {...field}
            select
            sx={{
              '& .MuiInputBase-root': {
                height: '32px',
                borderRadius: '4px',
                border: '1px solid #EAECF0',
                backgroundColor: '#F9FAFB',
                color: '#475467',
                margin: '0px',
              },
            }}
            variant="outlined"
            fullWidth
            size="small"
            onChange={e => {
              field.onChange(e);
              handleDropdownChange(e.target.value);
            }}
          >
            {node.data.listItems?.map((listItem: any, index: number) => (
              <MenuItem key={index} value={listItem.description}>
                {listItem.description}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
      )}
    />
  );

  const renderSingle = () => (
    <Controller
      name="inputText"
      control={control}
      render={({ field }) => (
        <FormControl
          component="fieldset"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <RadioGroup
            value={field.value || ''}
            onChange={e => {
              field.onChange(e);
              handleSingleChange(e.target.value);
            }}
          >
            {node.data.listItems?.map((listItem: any, index: number) => (
              <FormControlLabel
                key={index}
                value={listItem.description}
                control={
                  <Radio
                    size="small"
                  />
                }
                label={listItem.description}
                sx={{
                  borderRadius: '4px',
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px',
                    color: '#475467',
                  },
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}
    />
  );

  const renderMultiple = () => (
    <Controller
      name="inputText"
      control={control}
      render={({ field }) => {
        const currentValues = field.value
          ? field.value.split(',').map(item => item.trim())
          : [];

        return (
          <FormGroup
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            {node.data.listItems?.map((listItem: any, index: number) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    size="small"
                    checked={currentValues.includes(listItem.description)}
                    onChange={e => {
                      handleMultipleChange(
                        listItem.description,
                        e.target.checked,
                      );
                    }}
                  />
                }
                label={listItem.description}
                sx={{
                  borderRadius: '4px',
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px',
                    color: '#475467',
                  },
                }}
              />
            ))}
          </FormGroup>
        );
      }}
    />
  );

  const renderContent = () => {
    switch (node.data.typeQuestion) {
      case 'dropdown':
        return renderDropdown();
      case 'single':
        return renderSingle();
      case 'multiple':
        return renderMultiple();
      default:
        return null;
    }
  };

  const styles = node.data.styles;

  return (
    <FormProvider {...formsMethods}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: '8px',
        }}
      >
        {node.data.requiredField && (
          <Chip
            label="Required"
            size="small"
            style={{ marginBottom: '4px', width: 'fit-content' }}
          />
        )}

        <Typography
          style={{
            fontWeight: styles?.title?.isBold ? '700' : '400',
            fontStyle: styles?.title?.isItalic ? 'italic' : 'normal',
            textDecoration: styles?.title?.isUnderlined ? 'underline' : 'none',
            textAlign: styles?.title?.textAlign || 'left',
            fontFamily: styles?.title?.fontFamily || 'Roboto, sans-serif',
            fontSize: `${styles?.title?.fontSize || 16}px`,
            color: styles?.title?.color || '#1F2937',
            width: '100%',
            letterSpacing: '0.5px',
          }}
        >
          {node.data.title}
        </Typography>

        <Typography
          style={{
            fontWeight: styles?.question?.isBold ? '600' : '400',
            fontStyle: styles?.question?.isItalic ? 'italic' : 'normal',
            textDecoration: styles?.question?.isUnderlined
              ? 'underline'
              : 'none',
            textAlign: styles?.question?.textAlign || 'left',
            fontFamily: styles?.question?.fontFamily || 'Roboto, sans-serif',
            fontSize: `${(styles?.question?.fontSize || 16) * 0.875}px`,
            color: styles?.question?.color || '#475467',
            width: '100%',
            letterSpacing: '0.5px',
          }}
        >
          {node.data.question}
        </Typography>

        {renderContent()}
      </div>
    </FormProvider>
  );
}
